import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MdClose, MdUpload } from 'react-icons/md';
import toast from 'react-hot-toast';
import axios from '../lib/axios';
import { endpoints } from '../constants/endpoints';
import type { KbScope } from '../types';

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SCOPE_OPTIONS: { value: KbScope; label: string }[] = [
  { value: 'both', label: 'Both (site chat + email drafting)' },
  { value: 'chat', label: 'Site chat only' },
  { value: 'email', label: 'Email drafting only' },
];

export default function UploadModal({ isOpen, onClose }: UploadModalProps) {
  const [dragActive, setDragActive] = useState(false);
  const [kbScope, setKbScope] = useState<KbScope>('both');
  const queryClient = useQueryClient();

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('kb_scope', kbScope);
      return axios.post(endpoints.documents.upload, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => {
      toast.success('Document uploaded successfully');
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      onClose();
    },
  });

  const handleUpload = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    if (!validTypes.includes(file.type)) {
      toast.error('Only PDF and DOCX files are allowed');
      return;
    }

    const maxSize = 25 * 1024 * 1024; // 25MB
    if (file.size > maxSize) {
      toast.error('File size must be less than 25MB');
      return;
    }

    uploadMutation.mutate(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleUpload(e.dataTransfer.files);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Upload Document
          </h3>
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <MdClose size={20} />
          </button>
        </div>

        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Where can this document be used?
          </label>
          <select
            value={kbScope}
            onChange={(e) => setKbScope(e.target.value as KbScope)}
            disabled={uploadMutation.isPending}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {SCOPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`relative rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
            dragActive
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
          } ${uploadMutation.isPending ? 'pointer-events-none opacity-50' : ''}`}
        >
          <input
            type="file"
            accept=".pdf,.docx"
            onChange={(e) => handleUpload(e.target.files)}
            className="absolute inset-0 cursor-pointer opacity-0"
            disabled={uploadMutation.isPending}
          />
          <MdUpload
            size={32}
            className={`mx-auto mb-3 ${dragActive ? 'text-blue-500' : 'text-gray-400'}`}
          />
          <p className="text-sm text-gray-600">
            {uploadMutation.isPending
              ? 'Uploading...'
              : 'Drag & drop files here, or click to select'}
          </p>
          <p className="mt-1 text-xs text-gray-400">PDF or DOCX, max 25MB</p>
        </div>
      </div>
    </div>
  );
}
